import { Box, Divider, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TournamentBracketMatchGameStatus, TournamentBracketResult } from '@okletsplay/core';
import { DialogName, ModalName } from 'app/enums';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import {
  BracketSeedEntity,
  BracketSeedMatchGameEntity,
  KnockoutTournamentEntity,
} from 'app/interfaces/entity';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { uniq } from 'lodash';
import { TournamentMatchGameCodes } from 'modules/bracket/components/TournamentMatchDetailsModal/TournamentMatchGameCodes';
import { ACTIVE_MATCH_STATUSES, FEATURE_MATCH_GAME_STATUSES } from 'modules/bracket/constants';
import { useEditMatchGames } from 'modules/bracket/hooks';
import { useTournamentMatchResultsForm } from 'modules/bracket/hooks/use-tournament-match-results-form';
import { MatchGameResultFormField } from 'modules/bracket/interfaces';
import { tournamentBracketSelectorFactory } from 'modules/bracket/store/selectors';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import { UpdateMatchGameResultsRequestPayload } from 'modules/tournaments/store/current-tournament/interfaces/update-match-game-results.request';
import {
  runBracketMatch,
  updateMatchGameResults,
} from 'modules/tournaments/store/current-tournament/repository';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, SyntheticEvent, useCallback, useContext, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { modalsReducers } from 'app/store/modal/slice';
import { ConfirmationDialog } from 'shared/components/ConfirmationDialog';
import { MatchGameProperties, useTournamentMatchTitle } from 'modules/tournaments/hooks';
import { TournamentMatchAdminTab } from './TournamentMatchAdminTab';
import { TournamentMatchDetailsHeader } from './TournamentMatchDetailsHeader';
import { useStyles } from './TournamentMatchDetailsModal.styles';
import { TournamentMatchGamesList } from './TournamentMatchGamesList';
import { TournamentMatchTeamsList } from './TournamentMatchTeamsList';
import { ActiveMatchContext } from 'modules/bracket/providers';

const FORCE_RUN_MATCH_STATUSES = [
  TournamentBracketMatchGameStatus.Ready,
  TournamentBracketMatchGameStatus.Running,
];
export enum MatchDetailsTabs {
  Games,
  Teams,
  Admin,
  Lobby,
  Spectate,
}

const MATCH_DETAILS_TABS_LABEL: Record<
  MatchDetailsTabs,
  string | ((isSinglePlayer: boolean) => string)
> = {
  [MatchDetailsTabs.Games]: 'Games',
  [MatchDetailsTabs.Teams]: (isSinglePlayer: boolean) => (isSinglePlayer ? 'Players' : 'Teams'),
  [MatchDetailsTabs.Admin]: 'Admin',
  [MatchDetailsTabs.Lobby]: 'Lobby',
  [MatchDetailsTabs.Spectate]: 'Spectate',
};

const TournamentMatchDetailsModalComponent: FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { getTournamentMatchGameTitle } = useTournamentMatchTitle();

  const isLessThanXxs = useMediaQuery(theme.breakpoints.down('md'));

  const { form } = useTournamentMatchResultsForm();
  const {
    handleSubmit,
    getValues,
    formState: { isValid, isSubmitted },
  } = form;
  const { activeTab } = useContext(ActiveMatchContext);

  const tournament = useSelector(currentTournamentSelector.tournament) as KnockoutTournamentEntity;
  const isCurrentUserAdmin = useSelector(currentTournamentSelector.isCurrentUserAdmin);
  const isCurrentUserSpectator = useSelector(currentTournamentSelector.isCurrentUserSpectator);
  const isSinglePlayer = useSelector(currentTournamentSelector.isSinglePlayer);

  const matchId: number = useSelector(
    modalsSelectorFactory.useModalPayload(ModalName.TournamentMatchDetails)
  );
  const seed: BracketSeedEntity | null = useSelector(
    tournamentBracketSelectorFactory.useSeed(matchId)
  );

  const [value, setValue] = useState(activeTab || MatchDetailsTabs.Games);

  const isUpdating = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.UpdatingMatchGameResults,
  });

  const isRunning = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.RunBracketMatch,
  });

  const isReadyMatch: boolean = useMemo(() => {
    if (!seed) {
      return false;
    }

    if (!FORCE_RUN_MATCH_STATUSES.includes(seed.status)) {
      return false;
    }

    const runningGame = seed.matchGames.find(
      (game) => game.status === TournamentBracketMatchGameStatus.Running
    );

    if (runningGame) {
      return false;
    }

    const readyGame = seed.matchGames.find(
      (game) => game.status === TournamentBracketMatchGameStatus.Ready
    );
    return Boolean(readyGame);
  }, [seed]);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const matchGames = useMemo(() => {
    let featureGames: BracketSeedMatchGameEntity[] = [];

    if (ACTIVE_MATCH_STATUSES.includes(seed?.status as TournamentBracketMatchGameStatus)) {
      const featureGame = seed?.matchGames.find((game) => {
        if (!game.status) {
          return false;
        }

        return FEATURE_MATCH_GAME_STATUSES.includes(game.status);
      });

      featureGames = featureGame ? [featureGame] : [];
    }

    return (
      seed?.matchGames
        ?.filter((game) => {
          if (!game.status) {
            return false;
          }

          return !FEATURE_MATCH_GAME_STATUSES.includes(game.status);
        })
        .concat(featureGames) ?? []
    );
  }, [seed?.matchGames, seed?.status]);

  const { editableIndexes: editableResultIndexes } = useEditMatchGames(seed?.matchGames ?? []);

  const onSaveGameResultsHandler = useCallback(() => {
    const formData = getValues();
    const data: UpdateMatchGameResultsRequestPayload[] = formData.results
      .filter((_, index) => editableResultIndexes.includes(index))
      .map((matchResult) => {
        const results = matchResult.results.map((result) => result.score);
        const winnerScore = Math.max(...results);
        const isDraw = uniq(results).length === 1;

        if (isDraw) {
          return { matchGameId: matchResult.matchGameId, result: TournamentBracketResult.Draw };
        }

        const winner = matchResult.results.find(
          (result) => result.score === winnerScore
        ) as MatchGameResultFormField;
        const loser = matchResult.results.find(
          (result) => result.score !== winnerScore
        ) as MatchGameResultFormField;

        return {
          matchGameId: matchResult.matchGameId,
          winner,
          loser,
        };
      });

    dispatch(updateMatchGameResults({ tournamentId: tournament.id, data }));
  }, [dispatch, editableResultIndexes, getValues, tournament.id]);

  const openConfirmModal = useCallback(
    () => dispatch(modalsReducers.openModal({ name: DialogName.Confirmation })),
    [dispatch]
  );

  const onRunMatchHandler = useCallback(() => {
    if (!seed) {
      return;
    }

    dispatch(runBracketMatch({ tournamentId: tournament.id, matchId: seed.id }));
  }, [seed, dispatch, tournament.id]);

  const getDisplayMode = useCallback(
    (tab: MatchDetailsTabs) => (value === tab ? 'flex' : 'none'),
    [value]
  );
  const teamsLabel = MATCH_DETAILS_TABS_LABEL[MatchDetailsTabs.Teams];

  const game = useMemo<MatchGameProperties | null>(() => {
    const firstGame = seed?.matchGames?.[0];
    if (!firstGame) {
      return null;
    }

    return {
      roundNumber: seed?.round ?? 0,
      groupNumber: seed?.group ?? 0,
      matchNumber: firstGame.parent_id ?? 0,
      matchGameNumber: firstGame.number ?? 0,
    };
  }, [seed]);

  const title = getTournamentMatchGameTitle(game, tournament, { game: false });

  const actions = useMemo(() => {
    if (!isCurrentUserAdmin || value !== MatchDetailsTabs.Admin) {
      return null;
    }

    return (
      <Box display="flex" flexDirection="column" width="100%">
        {isReadyMatch && (
          <LoaderButton
            fullWidth
            size="medium"
            color="primary"
            variant="outlined"
            disabled={isRunning}
            onClick={openConfirmModal}
            className={classes.forceRunButton}
          >
            Force Advance Ready to Running Game
          </LoaderButton>
        )}
        <LoaderButton
          fullWidth
          size="medium"
          color="primary"
          variant="contained"
          onClick={handleSubmit(onSaveGameResultsHandler)}
          disabled={
            isUpdating ||
            (!isValid && isSubmitted) ||
            seed?.status !== TournamentBracketMatchGameStatus.Running
          }
        >
          Update Scores
        </LoaderButton>
      </Box>
    );
  }, [
    classes,
    handleSubmit,
    isCurrentUserAdmin,
    isReadyMatch,
    isRunning,
    isSubmitted,
    isUpdating,
    isValid,
    onSaveGameResultsHandler,
    openConfirmModal,
    seed?.status,
    value,
  ]);

  return (
    <Modal
      maxWidth="sm"
      withLogo={false}
      contentHeight={500}
      className={classes.root}
      isFooterHighlighted={false}
      isHeaderHighlighted={false}
      name={ModalName.TournamentMatchDetails}
      header={<Typography className={classes.title}>{title}</Typography>}
      actions={!isLessThanXxs ? <>{actions}</> : null}
    >
      <Box>
        <Box className={classes.header}>
          <TournamentMatchDetailsHeader seed={seed} tournamentStatus={tournament.status} />
        </Box>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          <Tab
            label={MATCH_DETAILS_TABS_LABEL[MatchDetailsTabs.Games]}
            value={MatchDetailsTabs.Games}
            disableRipple
          />
          <Tab
            label={typeof teamsLabel === 'string' ? teamsLabel : teamsLabel(isSinglePlayer)}
            value={MatchDetailsTabs.Teams}
            disableRipple
          />
          {isCurrentUserAdmin && (
            <Tab
              label={MATCH_DETAILS_TABS_LABEL[MatchDetailsTabs.Admin]}
              value={MatchDetailsTabs.Admin}
              disableRipple
            />
          )}
          {isCurrentUserAdmin ? (
            <Tab
              label={MATCH_DETAILS_TABS_LABEL[MatchDetailsTabs.Lobby]}
              value={MatchDetailsTabs.Lobby}
              disableRipple
            />
          ) : null}
          {isCurrentUserSpectator ? (
            <Tab
              label={MATCH_DETAILS_TABS_LABEL[MatchDetailsTabs.Spectate]}
              value={MatchDetailsTabs.Spectate}
              disableRipple
            />
          ) : null}
        </Tabs>
        <Divider />
      </Box>
      <Box className={classes.content} sx={{ display: getDisplayMode(MatchDetailsTabs.Games) }}>
        <TournamentMatchGamesList matchGames={matchGames ?? []} teams={seed?.teams ?? []} />
      </Box>
      <Box className={classes.content} sx={{ display: getDisplayMode(MatchDetailsTabs.Teams) }}>
        <TournamentMatchTeamsList teams={seed?.teams ?? []} isSinglePlayer={isSinglePlayer} />
      </Box>
      {isCurrentUserAdmin && (
        <Box className={classes.content} sx={{ display: getDisplayMode(MatchDetailsTabs.Admin) }}>
          <FormProvider {...form}>
            <form>
              <TournamentMatchAdminTab matchGames={seed?.matchGames ?? []} />
            </form>
          </FormProvider>
        </Box>
      )}
      {isCurrentUserAdmin ? (
        <Box className={classes.content} sx={{ display: getDisplayMode(MatchDetailsTabs.Lobby) }}>
          <TournamentMatchGameCodes matchGames={matchGames} sourceKey="formattedLobbyCode" />
        </Box>
      ) : null}
      {isCurrentUserSpectator ? (
        <Box
          className={classes.content}
          sx={{ display: getDisplayMode(MatchDetailsTabs.Spectate) }}
        >
          <TournamentMatchGameCodes matchGames={matchGames} sourceKey="formattedSpectatorCode" />
        </Box>
      ) : null}

      {isLessThanXxs && actions && (
        <Box pr={2} pl={2} pt={1.5} pb={1.5}>
          {actions}
        </Box>
      )}

      <ConfirmationDialog
        title={'Force Advance Ready to Running Game'}
        description="Are you sure you want to force advance the ready game to run the game?"
        onAccept={onRunMatchHandler}
      />
    </Modal>
  );
};

export const TournamentMatchDetailsModal = withModalOpenChecker(
  TournamentMatchDetailsModalComponent,
  ModalName.TournamentMatchDetails
);
